import { forwardRef, useCallback, useMemo } from 'react';
import {
  CircularProgress,
  FormControl,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useMapLayersState } from 'state/useMapLayersState';
import { useWebGL2State } from 'state/useWebGL2State';
import { useAuthState } from 'state/useAuthState';
import { usePoisState } from 'state/usePoisState';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import { useWaterLevelEnabled } from 'features/waterLevel/hooks/useWaterLevelEnabled';
import { isKeyInObject } from 'shared/types';
import { useExternalFeatureLayers } from 'features/mapping/hooks/useExternalFeatureLayers';
import { LayerGroup } from 'features/mapping/mapping.types';
import { useLayersDrawerState } from 'features/mapping/hooks/useLayersDrawerState';
import {
  LAYER_GROUPS,
  MapLayer,
  MapLayerDetail,
  MapLayers,
  MapLayersDetails,
} from 'features/mapping/mapping.constants';
import { useLightningLayerEnabled } from 'features/mapping/hooks/useLightningLayerEnabled';
import { WeatherSensorTypeSelector } from 'features/weatherStations/components/WeatherSensorTypeSelector';
import { MapBaseLayerSelector } from './MapBaseLayerSelector';
import { LayerGroupItem } from './LayerGroupItem';
import { LayerControlItem } from './LayerControlItem';
import { CollapsibleExternalLayerGroup } from './CollapsibleExternalLayerGroup';
import { LayerGroupsSection } from './LayerGroupsSection';
import { LayerTimelinePlayAction } from './LayerTimelinePlayAction';
import {
  canUserAccessMapLayer,
  getMapLayerPaywallLink,
  getVisibleGroupLayers,
  layerNameToEntityType,
  layerNameToPoiType,
  trackMapBaseLayerChangeToPosthog,
  trackMapLayerToggleToPosthog,
  type MapLayerAccessContext,
} from '../../../mapping.utils';
import { useLayerGroupsPanelStyles } from './layersDrawer.styles';

// The satellite-hotspots subtitle lists whichever flag-gated layers are showing:
// Landsat (GIS heat-detections flag) and NGFS (its own flag), on top of MODIS/VIIRS.
const getSatelliteHotspotsSubtitleKey = (
  isHeatDetectionsGisEnabled: boolean,
  isNgfsDetectionsEnabled: boolean,
): string => {
  const prefix = 'map.layers.general.satelliteHotspots';
  if (isNgfsDetectionsEnabled) {
    return isHeatDetectionsGisEnabled
      ? `${prefix}.subtitleWithNgfsAndLandsat`
      : `${prefix}.subtitleWithNgfs`;
  }
  return isHeatDetectionsGisEnabled
    ? `${prefix}.subtitleWithLandsat`
    : `${prefix}.subtitle`;
};

export const LayerGroups = forwardRef<HTMLDivElement>((_, ref): JSX.Element => {
  const { classes } = useLayerGroupsPanelStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('phone'));
  const {
    mapBaseLayer,
    mapLayers,
    setMapBaseLayer,
    setMapLayerVisibility,
    disabledLayers,
  } = useMapLayersState();
  const { showMembershipFeatures, showMembershipProFeatures, permissions } =
    useAuthState();
  const { hasWebGL2 } = useWebGL2State();
  const history = useHistory();
  const { selectedPoi, clearSelectedPoisOfType } = usePoisState();
  const { mapDrawerEntityForPoi, setMapDrawerEntityForPoi } =
    useMapDrawerEntityForPoiState();
  const showWaterLevel = useWaterLevelEnabled();
  const { showLightningLayer, showLightningInternalOnlyLabel } =
    useLightningLayerEnabled();

  const lightningMapLayerDetail: MapLayerDetail = {
    ...MapLayersDetails[MapLayers.LIGHTNING],
    internalOnly: showLightningInternalOnlyLabel,
  };

  const { externalFeatureLayers, isLoading: isLoadingExternalFeatureLayers } =
    useExternalFeatureLayers();
  const { section, openLayersSection, openLayersGroup, closeLayersDrawer } =
    useLayersDrawerState();
  const isHeatDetectionsGisEnabled = useReleaseFlag(
    'show_heat_detections_from_gis_in_frontend',
  );
  const isNgfsDetectionsEnabled = useReleaseFlag(
    'show_ngfs_detections_in_frontend',
  );

  const layerAccess = useMemo<MapLayerAccessContext>(
    () => ({ showMembershipFeatures, showMembershipProFeatures, permissions }),
    [showMembershipFeatures, showMembershipProFeatures, permissions],
  );
  const { isInternalUser } = permissions;

  const handleChangeLayer = useCallback(
    (mapLayer: MapLayer, details: MapLayerDetail, enabled: boolean): void => {
      const paywallLink = getMapLayerPaywallLink(
        mapLayer,
        details,
        layerAccess,
      );
      if (paywallLink) {
        history.push(paywallLink, {
          fromRoute: window.location.pathname + window.location.search,
        });
        return;
      }

      trackMapLayerToggleToPosthog(mapLayer, enabled);
      setMapLayerVisibility(mapLayer, enabled);
      if (!enabled) {
        if (selectedPoi && layerNameToPoiType[mapLayer]) {
          clearSelectedPoisOfType(layerNameToPoiType[mapLayer]);
        }
        if (
          mapDrawerEntityForPoi &&
          (layerNameToEntityType[mapLayer] === mapDrawerEntityForPoi.type ||
            // True for external feature layers
            mapLayer === mapDrawerEntityForPoi.type)
        ) {
          setMapDrawerEntityForPoi(null);
        }
      }
    },
    [
      clearSelectedPoisOfType,
      history,
      layerAccess,
      mapDrawerEntityForPoi,
      selectedPoi,
      setMapDrawerEntityForPoi,
      setMapLayerVisibility,
    ],
  );

  const renderControlItem = useCallback(
    (
      mapLayer: keyof typeof MapLayersDetails,
      details?: MapLayerDetail,
      // policyKey lets a control be disabled by a bucket id (e.g.
      // MapLayers.TEAM_LAYER for external/team layers) when the page policy
      // disallows the bucket — symmetric with isLayerVisible's policyKey.
      policyKey?: MapLayer,
      ExtraControlsComponent?: ({
        disabled,
      }: {
        disabled?: boolean;
      }) => JSX.Element,
    ): JSX.Element => {
      const layerDetails: MapLayerDetail =
        details || MapLayersDetails[mapLayer];

      const disabled =
        (layerDetails.requiresWebGL2 && !hasWebGL2) ||
        disabledLayers.includes(mapLayer) ||
        (policyKey !== undefined && disabledLayers.includes(policyKey));

      const checked =
        !disabled &&
        mapLayers.includes(mapLayer) &&
        canUserAccessMapLayer({
          minMembership: layerDetails.minMembership,
          requiredPermissions: layerDetails.requiredPermissions,
          ...layerAccess,
        });

      const title =
        'title' in layerDetails ? layerDetails.title : t(layerDetails.titleKey);
      const subtitle =
        'subtitle' in layerDetails
          ? layerDetails.subtitle
          : t(layerDetails.subtitleKey);

      return (
        <LayerControlItem
          key={mapLayer}
          title={title}
          subtitle={subtitle}
          disabled={disabled}
          checked={checked}
          onChange={(enabled): void =>
            handleChangeLayer(mapLayer, layerDetails, enabled)
          }
          mapLayer={mapLayer}
          internalOnly={layerDetails.internalOnly}
          minMembership={layerDetails.minMembership}
          privateLayer={'title' in layerDetails}
          action={
            layerDetails.timelineLayerId && (
              <LayerTimelinePlayAction layerId={layerDetails.timelineLayerId} />
            )
          }
        >
          {ExtraControlsComponent && (
            <ExtraControlsComponent disabled={disabled} />
          )}
        </LayerControlItem>
      );
    },
    [disabledLayers, handleChangeLayer, hasWebGL2, layerAccess, mapLayers, t],
  );

  const getGroupLayers = (layerGroup: LayerGroup): readonly MapLayer[] =>
    getVisibleGroupLayers(
      layerGroup,
      isHeatDetectionsGisEnabled,
      isNgfsDetectionsEnabled,
    );

  const getActiveLayersInGroupNumber = (layerGroup: LayerGroup): number => {
    return getGroupLayers(layerGroup).filter((layer: MapLayer) => {
      return (
        mapLayers.includes(layer) &&
        isKeyInObject(layer, MapLayersDetails) &&
        canUserAccessMapLayer({
          minMembership: MapLayersDetails[layer].minMembership,
          requiredPermissions: MapLayersDetails[layer].requiredPermissions,
          ...layerAccess,
        })
      );
    }).length;
  };

  return (
    <div
      style={{
        position: 'relative',
        paddingBottom: `max(env(safe-area-inset-bottom), 24px)`,
      }}
      ref={ref}
    >
      {!isPhone && (
        <IconButton
          aria-label={t('common.closeLayersDrawer')}
          className={classes.closeButton}
          onClick={closeLayersDrawer}
          color="inherit"
          size="large"
        >
          <CloseIcon />
        </IconButton>
      )}

      <div
        className={classes.contentContainer}
        data-testid="layers-map-type-section"
      >
        <FormControl fullWidth>
          <MapBaseLayerSelector
            value={mapBaseLayer}
            onChange={(layer): void => {
              trackMapBaseLayerChangeToPosthog(layer);
              setMapBaseLayer(layer);
            }}
          />
        </FormControl>
      </div>
      <div className={classes.mapTypeDivider} />

      <LayerGroupsSection
        section="general"
        title={t('map.layers.sections.general')}
        onToggleCollapse={() => openLayersSection('general')}
        expanded={section === 'general'}
      >
        <LayerGroupItem
          titleKey="map.layers.general.firePerimeters.title"
          subtitleKey="map.layers.general.firePerimeters.subtitle"
          onClick={(): void => openLayersGroup('firePerimeters')}
          totalLayers={LAYER_GROUPS.firePerimeters.length}
          activeLayers={getActiveLayersInGroupNumber('firePerimeters')}
        />
        <LayerGroupItem
          titleKey="map.layers.general.satelliteHotspots.title"
          subtitleKey={getSatelliteHotspotsSubtitleKey(
            isHeatDetectionsGisEnabled,
            isNgfsDetectionsEnabled,
          )}
          onClick={(): void => openLayersGroup('satelliteHotspots')}
          totalLayers={getGroupLayers('satelliteHotspots').length}
          activeLayers={getActiveLayersInGroupNumber('satelliteHotspots')}
        />
        {renderControlItem(MapLayers.FLIGHT_TRACKER)}
        {renderControlItem(MapLayers.POWER_OUTAGES)}
        {renderControlItem(MapLayers.CAMERAS)}
      </LayerGroupsSection>
      <LayerGroupsSection
        section="weather"
        title={t('map.layers.sections.weather')}
        onToggleCollapse={() => openLayersSection('weather')}
        expanded={section === 'weather'}
      >
        {renderControlItem(MapLayers.RED_FLAG_WARNINGS)}
        {renderControlItem(
          MapLayers.WEATHER_STATIONS,
          undefined,
          undefined,
          WeatherSensorTypeSelector,
        )}
        {renderControlItem(MapLayers.SURFACE_WIND)}
        {renderControlItem(MapLayers.PURPLE_AIR)}
        {showLightningLayer &&
          renderControlItem(MapLayers.LIGHTNING, lightningMapLayerDetail)}
      </LayerGroupsSection>
      <LayerGroupsSection
        section="flood"
        title={t('map.layers.sections.flood')}
        onToggleCollapse={() => openLayersSection('flood')}
        expanded={section === 'flood'}
      >
        {renderControlItem(MapLayers.FLOOD_WEATHER)}
        {showWaterLevel && renderControlItem(MapLayers.WATER_LEVEL)}
        {renderControlItem(MapLayers.FEMA_FLOOD_HAZARD)}
      </LayerGroupsSection>
      <LayerGroupsSection
        section="professional"
        title={t('map.layers.sections.professional')}
        onToggleCollapse={() => openLayersSection('professional')}
        expanded={section === 'professional'}
      >
        <LayerGroupItem
          titleKey="map.layers.general.federalLands.title"
          subtitleKey="map.layers.general.federalLands.subtitle"
          onClick={(): void => openLayersGroup('federalLands')}
          totalLayers={LAYER_GROUPS.federalLands.length}
          activeLayers={getActiveLayersInGroupNumber('federalLands')}
        />
        <LayerGroupItem
          titleKey="map.layers.general.criticalInfrastructure.title"
          subtitleKey="map.layers.general.criticalInfrastructure.subtitle"
          onClick={(): void => openLayersGroup('criticalInfrastructure')}
          totalLayers={LAYER_GROUPS.criticalInfrastructure.length}
          activeLayers={getActiveLayersInGroupNumber('criticalInfrastructure')}
        />
        {renderControlItem(MapLayers.EVACUATION_ZONES)}
        {isInternalUser && renderControlItem(MapLayers.EXTERNAL_GEO_EVENTS)}
      </LayerGroupsSection>
      {isLoadingExternalFeatureLayers && (
        <Stack p={2} alignItems="center">
          <CircularProgress size={32} />
        </Stack>
      )}
      {!isLoadingExternalFeatureLayers &&
        Boolean(externalFeatureLayers.length) && (
          <LayerGroupsSection
            section="team"
            title={t('map.layers.sections.team')}
            onToggleCollapse={() => openLayersSection('team')}
            expanded={section === 'team'}
          >
            {externalFeatureLayers
              .filter((layer) => !layer.parentFeatureLayer)
              .map((featureLayer) => {
                if (featureLayer.hasChildLayers) {
                  return (
                    <CollapsibleExternalLayerGroup
                      key={featureLayer.mapLayerId}
                      mapLayerId={featureLayer.mapLayerId}
                      renderControlItem={renderControlItem}
                      handleChangeLayer={handleChangeLayer}
                    />
                  );
                }

                return renderControlItem(
                  featureLayer.mapLayerId,
                  {
                    title: featureLayer.displayName,
                    subtitle: '',
                    requiresWebGL2: false,
                    minMembership: 'pro',
                  },
                  featureLayer.geomType === 'polygon'
                    ? MapLayers.TEAM_POLYGON_LAYER
                    : MapLayers.TEAM_LAYER,
                );
              })}
          </LayerGroupsSection>
        )}
    </div>
  );
});

LayerGroups.displayName = 'LayerGroups';
